<template>
  <div>
    <div class="header-row mb-3">
      <p class="donate-title is-family-secondary is-size-4 is-denim">
        {{ $t("donateRequest.donate") }}
      </p>
    </div>
    <p class="donate-sub has-text-weight-light mb-4 is-denim has-text-centered">
      {{ $t("donateRequest.donate-sub") }}
    </p>
    <carousel
      v-if="!loading && Object.keys(charities).length > 0"
      :items-to-show="numSlides"
      :wrap-around="Object.keys(charities).length > numSlides"
      class="charity-carousel"
      snapAlign="start"
      :breakpoints="{
        480: { itemsToShow: 1 },
        768: { itemsToShow: 1.5 },
        1024: { itemsToShow: 2.5 },
        1200: { itemsToShow: 3.5 }
      }"
    >
      <slide v-for="el in charities" :key="el.charityID" class="charity-slide">
        <div class="card">
          <div class="card-charity-image">
            <img :src="el.logo" class="charity-image" data-target="modal-image2" />
          </div>
          <div class="card-content">
            <div class="has-text-centered">
              <h4 class="has-text-centered">{{ el.charityName }}</h4>
              <p>
                {{ el.descriptionLang[$i18n.locale] }}
                <a :href="el.websiteUrl" target="_blank">{{ $t("donateRequest.learn-more") }}</a>
              </p>
            </div>
          </div>
          <div class="card-footer has-text-centered">
            <button class="button level is-info" @click.prevent="(e) => handleDonate(e, el.charityID)">
              {{ $t("donateRequest.donate-to", { name: el.charityName }) }}
            </button>
          </div>
        </div>
      </slide>
    </carousel>
    <div v-if="loading" class="has-text-centered">
      <Loading isInlineBlock isSmall />
    </div>
  </div>
</template>

<script setup>

import { ref, onMounted } from "vue";
import "vue3-carousel/dist/carousel.css";
import { Carousel, Slide } from "vue3-carousel";
import { useI18n } from "vue-i18n";

import { AuthService } from "@/revamp/firebase/config";
import { useFirebase } from "@/revamp/composables";
import { Loading } from "@/revamp/components";
import { logger, formatCurrencyLocalized } from "@/revamp/utils";

const emits = defineEmits(["update", "createModal", "sendVerification"]);

const APIService = useFirebase();
const { t, locale } = useI18n();

const user = ref();
const charities = ref({});
const loading = ref(false);
const numSlides = ref(1);

const init = async () => {
  loading.value = true;
  let authUser = AuthService.currentUser;
  user.value = await APIService.getUser(authUser.uid);
  try {
    const sortedCharitiesResponse = await APIService.getSortedCharitiesForUser(user.value.uid);
    charities.value = {};
    sortedCharitiesResponse.forEach((charity) => {
      if (charity.posted === true) {
        charities.value[charity.charityID] = charity;
      }
    });
  } catch (error) {
    logger.error("Error loading sorted charities:", error);
    const response = await APIService.listCharities();
    response.forEach((charity) => {
      charities.value[charity.charityID] = charity;
    });
  } finally {
    loading.value = false;
  }
};

onMounted(init);

const handleDonate = async (event, id) => {
  if (user.value.balance <= 0) {
    emits("createModal", {
      title: t("oops"),
      body: t("notEnough"),
      buttons: [
        {
          text: t("ok"),
          action: () => emits("createModal", {}),
        },
      ],
    });
    return;
  }
  emits("createModal", {
    title: t("confirmation"),
    body: t("donateRequest.proceed", { amount: formatCurrencyLocalized(locale.value, user.value.balance, user.value.currency) }),
    buttons: [
      {
        text: t("confirm"),
        action: () => {
          emits("createModal", {});
          confirmDonate(charities.value[id]);
        },
      },
      {
        text: t("cancel"),
        action: () => emits("createModal", {}),
      },
    ],
  });
};

const confirmDonate = async (charity) => {
  try {
    await APIService.donateBalance(user.value.uid, charity.charityID);
    emits("createModal", {
      title: t("success"),
      body: t("donateRequest.success"),
      buttons: [
        {
          text: t("close"),
          action: () => {
            emits("update");
            emits("createModal", {});
          },
        },
      ],
    });
  } catch (err) {
    emits("createModal", {
      title: t("failure"),
      body: t("donateRequest.error"),
      buttons: [
        {
          text: t("close"),
          action: () => emits("createModal", {}),
        },
      ],
    });
    logger.error(`DonateRequest.confirmDonate: Failed to complete donation. ${user.value.uid}. ${err.toString()}`);
  }
};

</script>

<style lang="scss" scoped>

.charity-carousel {
  margin: 15px;
  margin-bottom: 50px;
  max-height: 90vh;
  .charity-slide {
    padding: 15px;
    .card {
      min-width: 220px;
      display: flex;
      flex-direction: column;
      height: 100%;
      box-shadow: 5px 5px 15px 0px rgba(0, 0, 0, 0.38) !important;
      .card-charity-image {
        width: 100%;
        height: 35%;
        max-height: 250px;
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 10px;
        .charity-image {
          max-height: 100%;
        }
      }
      .card-content {
        flex: auto;
        height: 50%;
        overflow: auto;
      }
      .card-footer {
        padding-top: 14px;
        margin-bottom: 15px;
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 64px;
        height: auto;
        button {
          max-width: 100%;
          margin: 0 10px !important;
          word-wrap: break-word;
          white-space: normal !important;
          height: fit-content !important;
        }
      }
    }
  }
}
.header-row {
  width: 100%;
  max-height: 50px;
  display: flex;
  flex-direction: row;
  justify-content: center;
}
</style>
